/**
 * MarketSummaryCard Component
 *
 * Displays the daily smart summary with different states:
 * - Loading: skeleton animation
 * - No summary + notifications OFF: NotificationCTA only
 * - No summary + notifications ON: "Please wait" message
 * - Summary exists + notifications OFF: Summary card + NotificationCTA below
 * - Summary exists + notifications ON: Summary card only
 */

import { FC } from 'react';
import { css } from '@emotion/react';
import { formatDistanceToNowStrict } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { colors, spacing, fontStyles } from '../../styles';
import { SmartNotificationSummary, BulletPoint } from '../../apis/backend';
import NotificationCTA from '../NotificationCTA';

interface MarketSummaryCardProps {
  summary: SmartNotificationSummary | null;
  isLoading: boolean;
  notificationsEnabled: boolean;
}

const MarketSummaryCard: FC<MarketSummaryCardProps> = ({ summary, isLoading, notificationsEnabled }) => {
  // Loading state - compact spinner with message
  if (isLoading) {
    return (
      <div
        data-testid="market-summary-skeleton"
        css={css`
          background: ${colors.snow};
          border: 0.5px solid ${colors.cloud};
          border-radius: 16px;
          padding: ${spacing.deci} ${spacing.regular};
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: ${spacing.centi};
          min-height: 72px;

          html[data-theme='dark'] & {
            background: #242424;
            border-color: rgba(255, 255, 255, 0.06);
            box-shadow: none;
          }
        `}
      >
        <div
          css={css`
            width: 20px;
            height: 20px;
            border: 2px solid ${colors.cloud};
            border-top-color: ${colors.petrolBlue};
            border-radius: 50%;
            animation: spin 0.8s linear infinite;
            flex-shrink: 0;

            @keyframes spin {
              to {
                transform: rotate(360deg);
              }
            }

            html[data-theme='dark'] & {
              border-color: ${colors.tungsten};
              border-top-color: ${colors.skyBlue};
            }
          `}
        />
        <span
          css={css`
            ${fontStyles.dog};
            color: ${colors.tungsten};

            html[data-theme='dark'] & {
              color: ${colors.silver};
            }
          `}
        >
          Generating your market update...
        </span>
      </div>
    );
  }

  // No summary + notifications OFF - show NotificationCTA
  if (!summary && !notificationsEnabled) {
    return <NotificationCTA />;
  }

  // No summary + notifications ON - show "Please wait" message
  if (!summary && notificationsEnabled) {
    return (
      <div
        data-testid="market-summary-waiting"
        css={css`
          background: ${colors.snow};
          border: 0.5px solid ${colors.cloud};
          border-radius: 16px;
          padding: ${spacing.regular};
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
          text-align: center;

          html[data-theme='dark'] & {
            background: #242424;
            border-color: rgba(255, 255, 255, 0.06);
            box-shadow: none;
          }
        `}
      >
        <p
          css={css`
            ${fontStyles.dog}
            color: ${colors.tungsten};
            margin: 0;

            html[data-theme='dark'] & {
              color: ${colors.silver};
            }
          `}
        >
          Your daily market update will appear here. Please wait.
        </p>
      </div>
    );
  }

  // Summary exists - show card with full content
  // Also show NotificationCTA below if notifications are disabled
  return (
    <>
      <div
        data-testid="market-summary-card"
        css={css`
          background: ${colors.snow};
          border: 0.5px solid ${colors.cloud};
          border-radius: 16px;
          padding: ${spacing.regular};
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

          html[data-theme='dark'] & {
            background: #242424;
            border-color: rgba(255, 255, 255, 0.06);
            box-shadow: none;
          }
        `}
      >
        {/* Title */}
        {summary?.title && (
          <h3
            css={css`
              ${fontStyles.dog};
              font-weight: 500;
              color: var(--text-color);
              margin: 0 0 ${spacing.centi} 0;

              html[data-theme='dark'] & {
                color: ${colors.sugar};
              }
            `}
          >
            {summary.title}
          </h3>
        )}

        {/* Content - show bullet points if available, otherwise markdown */}
        <div
          css={css`
            ${fontStyles.thinMouse};
            color: var(--text-color);
            line-height: 1.4;
            margin-bottom: ${spacing.centi};
          `}
        >
          {summary?.bulletPoints && summary.bulletPoints.length > 0 ? (
            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: ${spacing.centi};
              `}
            >
              {summary.bulletPoints.map((bullet: BulletPoint, index: number) => (
                <div
                  key={index}
                  css={css`
                    display: flex;
                    align-items: flex-start;
                    gap: ${spacing.milli};
                  `}
                >
                  <span
                    css={css`
                      flex-shrink: 0;
                      font-size: 12px;
                      line-height: 1.5;
                    `}
                  >
                    {{ positive: '🟢', negative: '🔴', neutral: '⚪' }[bullet.sentiment] || '⚪'}
                  </span>
                  <span>{bullet.text}</span>
                </div>
              ))}
            </div>
          ) : (
            <div
              css={css`
                p {
                  margin: ${spacing.milli} 0;
                }

                ul,
                ol {
                  margin: ${spacing.milli} 0;
                  padding-left: ${spacing.centi};
                  list-style: none;
                }

                li {
                  margin: ${spacing.milli} 0;
                }

                strong {
                  font-weight: 600;
                }
              `}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{summary?.content || ''}</ReactMarkdown>
            </div>
          )}
        </div>

        {/* Timestamp */}
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: ${spacing.milli};
          `}
        >
          <span
            css={css`
              background: ${colors.fog};
              border: 0.5px solid ${colors.cloud};
              color: ${colors.silver};
              padding: 2px 6px;
              border-radius: 4px;
              font-size: 11px;
              font-weight: 500;

              html[data-theme='dark'] & {
                background: rgba(255, 255, 255, 0.12);
                border-color: rgba(255, 255, 255, 0.1);
                color: ${colors.sugar};
              }
            `}
          >
            Summary
          </span>
          <span
            css={css`
              ${fontStyles.thinMouse};
              color: ${colors.silver};
            `}
          >
            {formatDistanceToNowStrict(new Date(summary!.generatedAt), { addSuffix: true })}
          </span>
        </div>
      </div>

      {/* Show NotificationCTA if notifications are disabled */}
      {!notificationsEnabled && (
        <div
          css={css`
            margin-top: ${spacing.centi};
          `}
        >
          <NotificationCTA />
        </div>
      )}
    </>
  );
};

export default MarketSummaryCard;
